import { Currency } from "@redotech/money/currencies";
import { MoneyValue } from "@redotech/money/money-calculator";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { AdjustmentType, ReturnProductStrategy } from "../../return";

export interface ProductTotals<C extends Currency> {
  id: string;
  type: ReturnProductStrategy;
  /**
   * The return value for the product. This is the value that will be used in other return calculations,
   * all other product totals are used to calculate this value
   */
  returnValue: { amount: MoneyValue<C> };
  /** The new order value for the product. Will be zero unless the product has a variant exchange/exchange group */
  newOrderVariantValue: MoneyValue<C>;
  /** The adjustments applied to the product, including return flow and merchant manual adjustments */
  adjustments: { amount: MoneyValue<C>; type: AdjustmentType }[];
  originalValue: {
    /**
     * The discount for the product (generally just the line item discount divided by quantity, but can be influenced by
     * discount distribution, bundles, and returns of exchanges)
     */
    discount: MoneyValue<C>;
    /** The price after the discount has been applied */
    discountedPrice: MoneyValue<C>;
    price: MoneyValue<C>;
    tax: MoneyValue<C>;
  };
}

export interface ReturnTotals<C extends Currency> {
  returnValue: {
    /** The total return value of all products being returned */
    amount: MoneyValue<C>;
  };
  /** A list of totals associated with each product, including the return value and all the values that were used to calculate it */
  productTotalsList: ProductTotals<C>[];
  /** The amount of store credit that will be issued to the customer */
  storeCredit: {
    withoutOriginalOrderShipping: MoneyValue<C>;
    withOriginalOrderShipping: MoneyValue<C>;
  };
  /** The amount of refund that will be issued to the customer */
  refund: {
    withoutOriginalOrderShipping: MoneyValue<C>;
    withOriginalOrderShipping: MoneyValue<C>;
  };
  /** The amount of outstanding balance that the customer owes */
  outstandingBalance: MoneyValue<C>;
  newOrderTotals: {
    /** The total price of all exchange items (post discounts and taxes) */
    totalValue: MoneyValue<C>;
  };
  /** Return level adjustments, such as finalize flow and customer label upcharge fee */
  returnLevelAdjustments: { amount: MoneyValue<C>; type: AdjustmentType }[];
  /**
   * For returns with usePreDiscountPrice enabled, this is the sum of discounts across all return products
   * that will be applied as a use-it-or-lose-it shop now bonus
   */
  preDiscountShopNowBonus: MoneyValue<C>;
  finalSaleCredit: MoneyValue<C>;
}

interface ExchangeItemTotals<C extends Currency> {
  price: MoneyValue<C>;
  discount: MoneyValue<C>;
  discountedPrice: MoneyValue<C>;
}

export interface ProductSummaryTotals<C extends Currency>
  extends ProductTotals<C> {
  returnValue: ProductTotals<C>["returnValue"] & { preTax: MoneyValue<C> };
}

export interface ReturnSummaryTotals<C extends Currency>
  extends Omit<ReturnTotals<C>, "returnData"> {
  presentmentCurrency: C;
  shopCurrency: Currency;
  returnValue: ReturnTotals<C>["returnValue"] & {
    preTaxPreAdjustments: MoneyValue<C>;
  };
  newOrderTotals: ReturnTotals<C>["newOrderTotals"] & {
    preTax: MoneyValue<C>;
    exchangeItemTotalsList: ExchangeItemTotals<C>[];
  };

  productTotalsList: ProductSummaryTotals<C>[];
}

/**
 * Helper type to recursively serialize MoneyValue fields to numbers
 * This allows us to maintain a single source of truth for our types
 * while still being able to serialize them for RPC responses
 */
type SerializeMoneyValues<T> =
  T extends MoneyValue<Currency>
    ? number // Convert MoneyValue to number
    : T extends Array<infer U>
      ? Array<SerializeMoneyValues<U>> // Handle arrays recursively
      : T extends object
        ? { [K in keyof T]: SerializeMoneyValues<T[K]> } // Recurse into objects
        : T; // Keep primitives as-is

/**
 * Serialized version of ReturnSummaryTotals that can be sent over RPC
 */
export type SerializedReturnSummaryTotals = SerializeMoneyValues<
  ReturnSummaryTotals<Currency>
>;

export type SerializedProductSummaryTotals = SerializeMoneyValues<
  ProductSummaryTotals<Currency>
>;

export const ProductSummaryTotalsSchema: z.ZodType<SerializedProductSummaryTotals> =
  z.object({
    id: z.string(),
    type: createEnum(ReturnProductStrategy),
    returnValue: z.object({ amount: z.number(), preTax: z.number() }),
    newOrderVariantValue: z.number(),
    adjustments: z.array(
      z.object({ amount: z.number(), type: createEnum(AdjustmentType) }),
    ),
    originalValue: z.object({
      discount: z.number(),
      discountedPrice: z.number(),
      price: z.number(),
      tax: z.number(),
    }),
  });

export const ReturnSummaryTotalsSchema: z.ZodType<SerializedReturnSummaryTotals> =
  z.object({
    presentmentCurrency: createEnum(Currency),
    shopCurrency: createEnum(Currency),
    productTotalsList: z.array(ProductSummaryTotalsSchema),
    storeCredit: z.object({
      withoutOriginalOrderShipping: z.number(),
      withOriginalOrderShipping: z.number(),
    }),
    refund: z.object({
      withoutOriginalOrderShipping: z.number(),
      withOriginalOrderShipping: z.number(),
    }),
    outstandingBalance: z.number(),
    newOrderTotals: z.object({
      totalValue: z.number(),
      preTax: z.number(),
      exchangeItemTotalsList: z.array(
        z.object({
          price: z.number(),
          discount: z.number(),
          discountedPrice: z.number(),
        }),
      ),
    }),
    returnValue: z.object({
      amount: z.number(),
      preTaxPreAdjustments: z.number(),
    }),
    preDiscountShopNowBonus: z.number(),
    finalSaleCredit: z.number(),
    returnLevelAdjustments: z.array(
      z.object({ amount: z.number(), type: createEnum(AdjustmentType) }),
    ),
  });
